import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EditorialDetailModalFrame from '../EditorialDetailModalFrame';
import PostComments from '../PostComments';
import { eventService } from '../../../services/eventService';
import { resolveUserDisplayName } from '../../../utils/userIdentity';
import { trimToRecentThreads } from '../../../utils/commentThreads';
import './index.scss';

const COMMENT_PAGE_SIZE = 50;

async function fetchScheduledEntityCommentsPageSet(objectType, objectId, { allPages = false } = {}) {
  const loadedComments = [];
  let nextCount = 0;
  let page = 0;
  let hasMorePages = true;

  while (hasMorePages) {
    const response = await eventService.fetchScheduledEntityComments(
      objectType,
      objectId,
      page,
      COMMENT_PAGE_SIZE,
    );
    const pageComments = Array.isArray(response?.comments) ? response.comments : [];
    const responseTotal = typeof response?.total === 'number' ? response.total : null;
    nextCount = responseTotal ?? (page === 0 ? pageComments.length : loadedComments.length + pageComments.length);
    loadedComments.push(...pageComments);

    page += 1;
    hasMorePages = Boolean(
      allPages
        && responseTotal != null
        && loadedComments.length < responseTotal
        && pageComments.length > 0
    );
  }

  return {
    comments: loadedComments,
    count: nextCount,
  };
}

export default function ScheduledEntityCommentsModal({
  item,
  currentUserId,
  onClose,
  onItemMetaChange,
  readOnly = false,
}) {
  const { t } = useTranslation();
  const [comments, setComments] = useState(() => (item?.recentComments || []).slice());
  const [commentsCount, setCommentsCount] = useState(Number(item?.commentsCount) || 0);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const commentComposerRef = useRef(null);
  const loadedItemKeyRef = useRef(null);
  const canComment = !readOnly && item?.canComment === true;
  const itemKey = item?.id && item?.objectType ? `${item.objectType}:${item.id}` : null;
  const initialRecentComments = item?.recentComments;
  const initialCommentsCount = item?.commentsCount;

  const loadAllComments = useCallback(async () => {
    if (!item?.id || !item?.objectType) return;
    setIsLoadingComments(true);
    try {
      const { comments: loadedComments, count: nextCount } = await fetchScheduledEntityCommentsPageSet(
        item.objectType,
        item.id,
        { allPages: true },
      );

      setComments(loadedComments);
      setCommentsCount(nextCount);
      setCommentsExpanded(loadedComments.length > 0 && loadedComments.length >= nextCount);
      onItemMetaChange?.(item.id, {
        commentsCount: nextCount,
        recentComments: trimToRecentThreads(loadedComments),
      });
    } catch (error) {
      console.error('Failed to load scheduled entity comments', error);
    } finally {
      setIsLoadingComments(false);
    }
  }, [item?.id, item?.objectType, onItemMetaChange]);

  useEffect(() => {
    if (!itemKey) {
      loadedItemKeyRef.current = null;
      return;
    }
    if (loadedItemKeyRef.current === itemKey) {
      return;
    }
    loadedItemKeyRef.current = itemKey;
    setComments((initialRecentComments || []).slice());
    setCommentsCount(Number(initialCommentsCount) || 0);
    setCommentsExpanded(false);
    setCommentInput('');
    setReplyTo(null);
    loadAllComments();
  }, [initialCommentsCount, initialRecentComments, itemKey, loadAllComments]);

  const collapseComments = useCallback(() => {
    setComments((item?.recentComments || []).slice());
    setCommentsExpanded(false);
  }, [item?.recentComments]);

  const focusCommentComposer = useCallback(() => {
    window.setTimeout(() => {
      commentComposerRef.current?.focus();
    }, 0);
  }, []);

  const handleStartReply = useCallback((comment) => {
    if (readOnly || !canComment || !comment) return;
    const parentComment = comment.parentId
      ? comments.find((candidate) => candidate?.id === comment.parentId)
      : null;
    const target = parentComment || comment;
    if (!target?.id) return;
    const author = target.author || {};
    const authorName = resolveUserDisplayName(author, t('home.postCard.comments.anonymousAuthor'));
    const authorUsername = author.username || authorName;

    setReplyTo({
      commentId: target.id,
      authorName,
      authorUsername,
    });

    if (authorUsername) {
      const mention = `@${authorUsername}`;
      setCommentInput((currentValue) => {
        const current = currentValue || '';
        if (current === mention || current.startsWith(`${mention} `)) {
          return current;
        }
        return current ? `${mention} ${current}` : `${mention} `;
      });
    }
    focusCommentComposer();
  }, [canComment, comments, focusCommentComposer, readOnly, t]);

  const handleCancelReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  const handleSubmitComment = useCallback(async () => {
    const content = commentInput.trim();
    if (readOnly || !content || !canComment || !item?.id || !item?.objectType) return;
    const parentId = replyTo?.commentId ?? null;
    try {
      const newComment = await eventService.addScheduledEntityComment(
        item.objectType,
        item.id,
        content,
        parentId,
      );
      const commentToAdd = newComment && typeof newComment === 'object'
        ? { ...newComment, parentId: newComment.parentId ?? parentId }
        : newComment;
      const appendedComments = [...comments, commentToAdd];
      const nextCount = commentsCount + 1;
      const nextComments = commentsExpanded
        ? appendedComments
        : trimToRecentThreads(appendedComments);

      setCommentInput('');
      setReplyTo(null);
      setComments(nextComments);
      setCommentsCount(nextCount);
      onItemMetaChange?.(item.id, {
        commentsCount: nextCount,
        recentComments: trimToRecentThreads(appendedComments),
      });
    } catch (error) {
      console.error('Failed to add scheduled entity comment', error);
    }
  }, [
    canComment,
    commentInput,
    comments,
    commentsCount,
    commentsExpanded,
    item?.id,
    item?.objectType,
    onItemMetaChange,
    readOnly,
    replyTo?.commentId,
  ]);

  const handleEditComment = useCallback(async (comment, nextContent) => {
    const content = (nextContent || '').trim();
    if (readOnly || !content || !comment?.id || !item?.id || !item?.objectType) return;
    try {
      const updated = await eventService.updateScheduledEntityComment(
        item.objectType,
        item.id,
        comment.id,
        content,
      );
      const updatedComment = updated && typeof updated === 'object'
        ? { ...comment, ...updated, parentId: updated.parentId ?? comment.parentId ?? null }
        : { ...comment, content };
      const nextComments = comments.map((candidate) => (
        candidate?.id === comment.id ? { ...candidate, ...updatedComment } : candidate
      ));
      setComments(nextComments);
      onItemMetaChange?.(item.id, {
        recentComments: trimToRecentThreads(nextComments),
      });
    } catch (error) {
      console.error('Failed to update scheduled entity comment', error);
      throw error;
    }
  }, [comments, item?.id, item?.objectType, onItemMetaChange, readOnly]);

  const handleDeleteComment = useCallback(async (comment) => {
    if (readOnly || !comment?.id || !item?.id || !item?.objectType) return;
    const commentId = String(comment.id);
    try {
      await eventService.deleteScheduledEntityComment(item.objectType, item.id, commentId);
      const removedIds = new Set([commentId]);
      if (!comment.parentId) {
        comments.forEach((candidate) => {
          if (candidate?.parentId != null && String(candidate.parentId) === commentId && candidate?.id != null) {
            removedIds.add(String(candidate.id));
          }
        });
      }
      let nextComments = comments.filter((candidate) => (
        candidate?.id == null || !removedIds.has(String(candidate.id))
      ));
      let nextCount = Math.max(0, commentsCount - removedIds.size);

      if (!comment.parentId) {
        try {
          const {
            comments: refreshedComments,
            count: refreshedCount,
          } = await fetchScheduledEntityCommentsPageSet(
            item.objectType,
            item.id,
            { allPages: commentsExpanded },
          );
          nextCount = refreshedCount;
          nextComments = commentsExpanded ? refreshedComments : trimToRecentThreads(refreshedComments);
        } catch (refreshError) {
          console.error('Failed to refresh scheduled entity comments after delete', refreshError);
        }
      }

      setComments(nextComments);
      setCommentsCount(nextCount);
      if (replyTo?.commentId && removedIds.has(String(replyTo.commentId))) {
        setReplyTo(null);
      }
      onItemMetaChange?.(item.id, {
        commentsCount: nextCount,
        recentComments: trimToRecentThreads(nextComments),
      });
    } catch (error) {
      console.error('Failed to delete scheduled entity comment', error);
      throw error;
    }
  }, [
    comments,
    commentsCount,
    commentsExpanded,
    item?.id,
    item?.objectType,
    onItemMetaChange,
    readOnly,
    replyTo?.commentId,
  ]);

  const handleCommentKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && replyTo) {
      event.preventDefault();
      handleCancelReply();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmitComment();
    }
  }, [handleCancelReply, handleSubmitComment, replyTo]);

  if (!item) return null;

  return (
    <EditorialDetailModalFrame
      onClose={onClose}
      ariaLabel={t('home.postComments.count', { count: commentsCount })}
      closeLabel={t('common.close', 'Close')}
      bodyClassName="scheduled-entity-comments-modal"
    >
      <header className="scheduled-entity-comments-modal__header">
        <span className="scheduled-entity-comments-modal__eyebrow">
          {item.objectType === 'SESSION'
            ? t('home.postCard.relatedObject.type.session', 'Session')
            : t('home.postCard.relatedObject.type.event', 'Event')}
        </span>
        <h2 className="scheduled-entity-comments-modal__title">{item.title}</h2>
      </header>
      <PostComments
        isVisible
        comments={comments}
        commentsCount={commentsCount}
        isExpanded={commentsExpanded}
        isLoading={isLoadingComments}
        canComment={canComment}
        commentValue={commentInput}
        onCommentChange={setCommentInput}
        onCommentKeyDown={handleCommentKeyDown}
        onSubmitComment={handleSubmitComment}
        onStartReply={handleStartReply}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        replyTo={replyTo}
        onCancelReply={handleCancelReply}
        composerRef={commentComposerRef}
        onLoadAll={loadAllComments}
        onCollapse={collapseComments}
        postAuthorId={item.ownerId}
        currentUserId={readOnly ? null : currentUserId}
        readOnly={readOnly}
      />
    </EditorialDetailModalFrame>
  );
}

ScheduledEntityCommentsModal.propTypes = {
  item: PropTypes.object,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onItemMetaChange: PropTypes.func,
  readOnly: PropTypes.bool,
};
